import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import marketplaceOverrideService from '../services/marketplaceOverrideService';
import marketplaceService from '../services/marketplaceService';
import marketplacesettingsService from '../services/marketplacesettingsService';
import './AddCorporateProfilePage.css';   // acp-* page/card/field/footer classes
import './MarketplaceOverrides.css';

// Add / edit one marketplace override. A full page, not a modal: an add form in an overlay cannot be
// linked to, breaks browser history and has nowhere to grow.
//
// Entity-agnostic like the tab that opens it — entityType comes from the route, entityId from the path.

// A SELECT setting's allowed values arrive as "Y:Y|N:N" (label:value, comma or pipe separated). Same
// parse as CredentialsTab's SettingField, so a setting renders identically wherever it is edited.
const parseOptions = (availableValues) =>
    (availableValues || '').split(/[,|]/).map(entry => {
        const [label, value] = entry.trim().split(':');
        return { label: (label || '').trim(), value: (value ?? label ?? '').trim() };
    }).filter(o => o.label);

const MarketplaceOverrideFormPage = ({ entityType = 'account' }) => {
    const navigate = useNavigate();
    const params = useParams();
    const entityId = params.id;
    const overrideId = params.overrideId;
    const isEdit = !!overrideId;

    // Where Cancel and Save return to: the entity's own page, on the tab the user came from — never an
    // intermediate list.
    const returnTo = `/accounts/${entityId}?tab=marketplaceoverrides`;

    const [marketplaces, setMarketplaces] = useState([]);
    const [settings, setSettings] = useState([]);
    const [marketplaceId, setMarketplaceId] = useState('');
    const [settingId, setSettingId] = useState('');
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // The catalog read, not the admin one: this screen is about a chain's own configuration and must work
    // for a chain administrator, who has no access to the admin API.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await marketplaceService.getCatalog({ limit: 500 });
                const list = res.marketplaces || res.settings || [];
                // Same filter as CredentialsTab and V2ConnectorForm: real, active marketplaces only.
                if (!cancelled) setMarketplaces(list.filter(m => m.MARKETPLACEID > 0 && m.SYSTEMACTIVE === 'Y'));
            } catch (err) {
                if (!cancelled) setError('Failed to load marketplaces');
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Setting definitions for the chosen marketplace. Sanitised schema — definitions only, no values.
    const loadSettings = useCallback(async (mid) => {
        if (!mid) { setSettings([]); return; }
        try {
            const res = await marketplacesettingsService.getSchema({ MARKETPLACEID: mid, limit: 500 });
            setSettings(res.settings || []);
        } catch (err) {
            setError('Failed to load settings for that marketplace');
            setSettings([]);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!isEdit) { setLoading(false); return; }
            try {
                const res = await marketplaceOverrideService.getById(overrideId);
                const o = res.data?.override;
                if (cancelled || !o) return;
                setMarketplaceId(String(o.marketplace_id));
                setSettingId(String(o.marketplace_setting_id));
                setValue(o.setting_value || '');
                await loadSettings(o.marketplace_id);
            } catch (err) {
                if (!cancelled) setError(err.response?.data?.error?.message || 'Failed to load override');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isEdit, overrideId, loadSettings]);

    const handleMarketplaceChange = (mid) => {
        setMarketplaceId(mid);
        // The setting belongs to the marketplace, so it cannot survive a change of marketplace — keeping
        // it would submit a pair the API rejects, with the mismatch invisible on screen.
        setSettingId('');
        setValue('');
        loadSettings(mid);
    };

    const selectedSetting = useMemo(
        () => settings.find(s => String(s.MARKETPLACESETTINGID) === String(settingId)) || null,
        [settings, settingId]);

    const options = useMemo(
        () => (selectedSetting?.DATATYPE === 'SELECT' ? parseOptions(selectedSetting.AVAILABLEVALUES) : []),
        [selectedSetting]);

    const handleSave = async () => {
        if (!marketplaceId || !settingId) {
            setError('Choose a marketplace and a setting');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            if (isEdit) {
                await marketplaceOverrideService.update(overrideId, { setting_value: value });
            } else {
                await marketplaceOverrideService.create({
                    entityType,
                    entityId: parseInt(entityId, 10),
                    marketplace_id: parseInt(marketplaceId, 10),
                    marketplace_setting_id: parseInt(settingId, 10),
                    setting_value: value,
                });
            }
            navigate(returnTo);
        } catch (err) {
            setError(err.response?.data?.error?.message || 'Failed to save override');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div className="acp-card-header">
                    <h1 className="acp-title">{isEdit ? 'Edit Marketplace Override' : 'Add Marketplace Override'}</h1>
                </div>

                <div className="acp-divider" />

                <div className="acp-body">
                    {loading ? (
                        <div className="mko-loading">Loading…</div>
                    ) : (
                        <>
                            <div className="acp-field">
                                <label className="acp-label">Marketplace <span className="acp-required">*</span></label>
                                <select
                                    className="acp-input"
                                    value={marketplaceId}
                                    onChange={e => handleMarketplaceChange(e.target.value)}
                                    disabled={isEdit}
                                >
                                    <option value="">Select a marketplace…</option>
                                    {marketplaces.map(m => (
                                        <option key={m.MARKETPLACEID} value={m.MARKETPLACEID}>{m.SYSTEMNAME}</option>
                                    ))}
                                </select>
                                {isEdit && (
                                    <span className="mko-hint">
                                        The marketplace and setting are fixed on an existing override. Delete it and add
                                        another to point at a different setting.
                                    </span>
                                )}
                            </div>

                            <div className="acp-field">
                                <label className="acp-label">Setting <span className="acp-required">*</span></label>
                                <select
                                    className="acp-input"
                                    value={settingId}
                                    onChange={e => setSettingId(e.target.value)}
                                    disabled={isEdit || !marketplaceId}
                                >
                                    <option value="">{marketplaceId ? 'Select a setting…' : 'Choose a marketplace first'}</option>
                                    {settings.map(s => (
                                        <option key={s.MARKETPLACESETTINGID} value={s.MARKETPLACESETTINGID}>
                                            {s.SETTINGNAME}{s.type ? ` (${s.type})` : ''}
                                        </option>
                                    ))}
                                </select>
                                {selectedSetting?.HELPERTEXT && (
                                    <span className="mko-hint">{selectedSetting.HELPERTEXT}</span>
                                )}
                            </div>

                            <div className="acp-field">
                                <label className="acp-label">Value</label>
                                {options.length ? (
                                    <select className="acp-input" value={value} onChange={e => setValue(e.target.value)}>
                                        <option value="">Select…</option>
                                        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                ) : (
                                    <input
                                        className="acp-input"
                                        type="text"
                                        value={value}
                                        onChange={e => setValue(e.target.value)}
                                        placeholder="Value for this chain"
                                    />
                                )}
                                <span className="mko-hint">
                                    This replaces the marketplace default wherever the setting is read for this {entityType}.
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {error && <div className="mko-error" style={{ margin: '0 2rem 1rem' }}>{error}</div>}

                <div className="acp-footer">
                    <button className="acp-btn-cancel" onClick={() => navigate(returnTo)} disabled={saving}>
                        Cancel
                    </button>
                    <button className="acp-btn-create" onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MarketplaceOverrideFormPage;
